import { useNavigate } from 'react-router-dom';

import { RecordPageShell } from '~/components/layout';
import ProviderKeyForm from '~/components/pages/notifications/provider-keys/ProviderKeyForm';
import { useRecordDraft } from '~/hooks/useRecordDraft';
import { useRecordSave } from '~/hooks/useRecordSave';
import { useUnsavedGuard } from '~/hooks/useUnsavedGuard';
import {
  emptyProviderKeyForm,
  type ProviderKeyFormValues,
  toProviderKeyInput,
} from '~/lib/notification/providerKeyForm';
import { useCreateProviderKeyMutation } from '~/modules/query/notification/providerKeys';
import { routes } from '~/app/routes';

export default function ProviderKeyCreatePage() {
  const navigate = useNavigate();
  const [createProviderKey] = useCreateProviderKeyMutation();
  const { draft, patch, dirty } = useRecordDraft<null, ProviderKeyFormValues>(null, {
    id: 'new',
    toDraft: emptyProviderKeyForm,
  });
  const guard = useUnsavedGuard(dirty);

  // A duplicate (409) comes back as the service's sentence and shows in the form's alert.
  const save = useRecordSave<ProviderKeyFormValues>({
    save: async (form) => {
      const created = await createProviderKey(toProviderKeyInput(form)).unwrap();
      guard.release();
      navigate(routes.notificationProviderKey(created.id), { replace: true });
    },
    success: 'Provider key created',
    failure: 'The provider key could not be created. Try again.',
  });

  return (
    <RecordPageShell
      title="Create provider key"
      breadcrumbs={[
        { label: 'Notifications' },
        { label: 'Provider keys', href: routes.notificationProviderKeys },
        { label: 'Create provider key' },
      ]}
      guard={guard}
    >
      {draft && (
        <ProviderKeyForm
          draft={draft}
          patch={patch}
          dirty={dirty}
          busy={save.busy}
          error={save.error}
          onSubmit={() => save.submit(draft)}
        />
      )}
    </RecordPageShell>
  );
}
